import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Field, FlexForm, Inscription, Preloader, Text, useResponsive } from '@common-fe/common-fe';

import {
  PaymentMethod,
  PaymentMethodVerificationStatus,
} from '@/modules/transaction/components/ReimburseMe/types/payment.types';
import { PlaidDirectDepositBanner } from '@/modules/user/components/ManageProfile/components/ReimbursementOptions/components/PaymentMethodsList/PlaidDirectDepositBanner';
import spacings from '@/styles/spacings';

import { StepsStates } from './Immediately';
import PaymentMethodField from './PaymentMethodField';

export interface DefaultPaymentMethodBlockProps {
  isLoading: boolean;
  activeMethod: PaymentMethod | undefined;
  paymentEmployeeFee: string | undefined;
  isVerificationFailed: boolean;
  isVerificationPending: boolean;
  onAddOrChangeClick: () => void;
}

interface Props {
  isVerifyAccountFlow?: boolean;
  isLoading?: boolean;
  isFormDataLoading?: boolean;
  isBankAccountLoading?: boolean;
  addCheckPaymentLoading?: boolean;
  isPaymentMethodsLoading?: boolean;
  defaultStep?: StepsStates;
  type: 'full' | 'small';
  isTitleHidden?: boolean;
  fields: Field[];
  currentPaymentMethod?: PaymentMethod | null;
  paymentEmployeeFee?: string;
  setSteps: (step: StepsStates) => void;
  updateCurrentMethodAfterVerification: () => Promise<void>;
  setIsVerifyAccountFlow: (value: boolean) => void;
  renderWrapper?: (props: DefaultPaymentMethodBlockProps) => React.ReactNode;
}

const ImmediatelyButton: React.FC<Props> = ({
  isVerifyAccountFlow,
  isLoading,
  isFormDataLoading,
  isBankAccountLoading,
  addCheckPaymentLoading,
  isPaymentMethodsLoading,
  defaultStep,
  type,
  isTitleHidden,
  fields,
  currentPaymentMethod,
  paymentEmployeeFee,
  setSteps,
  updateCurrentMethodAfterVerification,
  setIsVerifyAccountFlow,
  renderWrapper,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  const isAllLoading = !isVerifyAccountFlow &&
    (isLoading ||
      isFormDataLoading ||
      isBankAccountLoading ||
      addCheckPaymentLoading ||
      isPaymentMethodsLoading);

  const isVerificationFailed = useMemo(() =>
    currentPaymentMethod?.verificationStatus === PaymentMethodVerificationStatus.VERIFICATION_FAILED ||
    currentPaymentMethod?.verificationStatus === PaymentMethodVerificationStatus.EXPIRED,
  [currentPaymentMethod]);

  const isVerificationPending = useMemo(() =>
    currentPaymentMethod?.verificationStatus === PaymentMethodVerificationStatus.PENDING ||
    currentPaymentMethod?.verificationStatus === PaymentMethodVerificationStatus.PENDING_MANUAL,
  [currentPaymentMethod]);

  if (renderWrapper) {
    return (
      <>
        {renderWrapper({
          isLoading: Boolean(isAllLoading),
          activeMethod: currentPaymentMethod || undefined,
          paymentEmployeeFee,
          isVerificationFailed,
          isVerificationPending,
          onAddOrChangeClick: () => setSteps(StepsStates.depositOrDebit),
        })}
      </>
    );
  }

  if (isAllLoading) {
    return <>{defaultStep ? null : <Preloader testId="payment-method-loader" />}</>;
  }

  return (
    <>
      {type === 'full' && !defaultStep && (
        <>
          {isTitleHidden ? null : (
            <Inscription
              data-testId="payment-method-full-card_title"
              margin={{ bottom: 'spacing24' }}
              color="textBody"
              size="20px"
              lineHeight="28px"
            >
              {t('How Should We Reimburse You?')}
            </Inscription>
          )}
          <FlexForm paddingless fields={fields} contentStyles={{ margin: 0 }} isThinMode />
          {isMobile ? undefined : <Box height={spacings.l} />}
        </>
      )}
      {type === 'small' && !defaultStep && (
        <>
          <Text
            data-testId="payment-method-small-card_title"
            weight={700}
            size="16px"
            margin={{ bottom: 'spacing12' }}
          >
            {t('We’ll pay the claim to your default payment method')}
          </Text>
          <PaymentMethodField onAdd={() => setSteps(StepsStates.depositOrDebit)} />
          {isMobile ? undefined : <Box height={spacings.l} />}
        </>
      )}
      {currentPaymentMethod?.verificationStatus
      && currentPaymentMethod?.verificationStatus !== PaymentMethodVerificationStatus.VERIFIED ? (
        <Box margin={{ top: 'spacing16' }}>
          <PlaidDirectDepositBanner
            status={currentPaymentMethod?.verificationStatus}
            withoutButton
            highlightedTop
            accountId={currentPaymentMethod?.accountId}
            refetch={updateCurrentMethodAfterVerification}
            additionalOnCLickAction={() => setIsVerifyAccountFlow(true)}
          />
        </Box>
      ) : null}
    </>
  );
};

export default ImmediatelyButton;
